<template>
    <div class="sc-message">
        <div
            :class="{
                sent: message.author === authUser.id,
                received: message.author !== authUser.id && message.type !== 'system',
                system: message.type === 'system',
            }"
            class="sc-message--content"
        >
            <e-tooltip
                v-if="message.type !== 'system' && message.author !== authUser.id"
                :label="authorName"
                position="is-right"
            >
                <user-avatar :img-url="chatImageUrl" :title="authorName" size="32x32" />
            </e-tooltip>

            <TextMessage
                v-if="message.type === 'text'"
                :data="message.data"
                :message-colors="determineMessageColors()"
                :message-styling="messageStyling"
            />
            <EmojiMessage v-else-if="message.type === 'emoji'" :data="message.data" />
            <FileMessage
                v-else-if="message.type === 'file'"
                :data="message.data"
                :message-colors="determineMessageColors()"
            />
            <TypingMessage
                v-else-if="message.type === 'typing'"
                :message-colors="determineMessageColors()"
                class="m-t-md"
                style="margin-left: 0.6rem"
            />
            <SystemMessage
                v-else-if="message.type === 'system'"
                :data="message.data"
                :message-colors="determineMessageColors()"
            />
        </div>
    </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import EmojiMessage from '@/components/chat/system/EmojiMessage.vue';
import FileMessage from '@/components/chat/system/FileMessage.vue';
import SystemMessage from '@/components/chat/system/SystemMessage.vue';
import TextMessage from '@/components/chat/system/TextMessage.vue';
import TypingMessage from '@/components/chat/system/TypingMessage.vue';
import Avatar from '@/components/elements/Avatar.vue';

const { mapState } = createNamespacedHelpers('Chat');

export default {
    name: 'ChatMessage',

    components: {
        UserAvatar: Avatar,
        TextMessage,
        FileMessage,
        EmojiMessage,
        TypingMessage,
        SystemMessage,
    },

    props: {
        message: {
            type: Object,
            required: true,
        },
        chatImageUrl: {
            type: String,
            default: '',
        },
        colors: {
            type: Object,
            required: true,
        },
        authorName: {
            type: String,
            default: '',
        },
        messageStyling: {
            type: Boolean,
            required: true,
        },
    },

    computed: {
        ...mapState(['authUser']),
    },

    methods: {
        sentColorsStyle() {
            return {
                color: this.colors.sentMessage.text,
                backgroundColor: this.colors.sentMessage.bg,
            };
        },

        receivedColorsStyle() {
            return {
                color: this.colors.receivedMessage.text,
                backgroundColor: this.colors.receivedMessage.bg,
            };
        },

        determineMessageColors() {
            return this.message.author === this.authUser.id ? this.sentColorsStyle() : this.receivedColorsStyle();
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.sc-message {
    margin: auto;
    padding-bottom: $column-gap;
    display: flex;

    &:last-child,
    &:nth-last-child(2) {
        padding-bottom: 0;
    }
}

.sc-message--content {
    align-items: center;
    display: flex;
    width: 100%;
}

.sc-message--content.sent {
    justify-content: flex-end;
}

.sc-message--content.system {
    justify-content: center;
}

.sc-message--content.sent .sc-message--avatar {
    display: none;
}

.sc-message--meta {
    font-size: xx-small;
    margin-bottom: 0;
    color: white;
    text-align: center;
}

.sc-message--text {
    padding: $size-10 $size-8;
    border-radius: $radius-large;
    font-size: 1rem;
    white-space: pre-wrap;
}

.sc-message--content.sent .sc-message--text {
    color: $primary-invert !important;
    background-color: $primary !important;
    max-width: calc(100% - 70px);
    word-wrap: break-word;
}

.sc-message--text code {
    font-family: 'Courier New', Courier, monospace !important;
}

.sc-message--content.received .sc-message--text {
    color: findColorInvert($grey-lighter) !important;
    background-color: $grey-lighter !important;
    margin: 0 $size-2 0 $size-10;
}
</style>
